import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  line, Color, Size, mergeFieldConflicts, mergeFieldText, sortRows, t,
  type ColumnDef, type DuplicatePairRow, type DuplicateRecordFacts, type DuplicateRecordType, type DuplicateReportAnswer, type MergeMoveList, type MergePreviewAnswer, type PageSize, type SortState, viewerScopeKey,
} from '@bms2/shared';
import { Button } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { ListViewer } from '../../components/composites/ListViewer';
import { FormFrame } from '../../components/forms/FormFrame';
import { Modal } from '../../components/modals/Modal';
import { PageModule } from '../../shell/PageModule';
import { get, post, useApi } from '../records/api';
import styles from './DuplicatesPage.module.css';
import { MergeTable } from './MergeTable';
import { MergedView, UNDO_ROUTE_PREFIX } from './MergedView';
import { UndoMergeWindow } from './UndoMergeWindow';

/**
 * The Duplicates report at `/companies/duplicates` (piece 10; decision 1484),
 * the page `queue:duplicate-review` names. Every pair of companies and of
 * people the duplicate check flags, strongest match first, each with the
 * reason in the check's own words. 🔒 Nothing merges until a person presses
 * Merge and names the survivor; an owner of both merges at once, and anybody
 * else's press goes to the approver.
 */
export const DUPLICATES_ROUTE = '/companies/duplicates';
/** T3422: the report opened on one pair, by the two companies' ids, as the import's cleanup list links it. */
export const DUPLICATES_PAIR_ROUTE = `${DUPLICATES_ROUTE}/pair`;
export const DUPLICATES_PATH = '/duplicates';
export const DUPLICATES_MERGE_ALL_EXACT_PATH = '/duplicates/merge-all-exact';
export const DUPLICATES_DISMISSALS_PATH = '/duplicates/dismissals';
export const DUPLICATES_MERGE_PREVIEW_PATH = '/duplicates/merge-preview';
/** The merge step's preview (decision 1491): both records' field values and what moves, for one survivor. */
export const mergePreviewPath = (recordType: DuplicateRecordType, losingId: string, intoId: string): string =>
  `${DUPLICATES_MERGE_PREVIEW_PATH}?${new URLSearchParams({ recordType, losingId, intoId }).toString()}`;
/** The two merge routes: the record in the path is merged into the one the body names. */
export const recordMergePath = (recordType: DuplicateRecordType, id: string): string => `${recordType === 'CompanyRecord' ? '/companies' : '/people'}/${id}/merge`;

/** The Merge cell's own address: a link cell, so the row's Merge reads as a press; the page opens the step rather than leaving. */
const MERGE_ROUTE_PREFIX = `${DUPLICATES_ROUTE}/merge/`;

type MergeAnswer = { outcome: 'Merged'; recordMergeId: string; survivorId: string } | { outcome: 'Requested'; requestId: string; escalationReason: string | null };

interface PairLine {
  key: string;
  first: string;
  firstId: string;
  second: string;
  secondId: string;
  reason: string;
  flags: string;
  sites: string;
  documents: string;
  created: string;
  newerBy: string;
  action: string;
  row: DuplicatePairRow;
}

const VIEWS = [
  { id: 'companies', name: t('duplicates.view.companies') },
  { id: 'people', name: t('duplicates.view.people') },
  // 🔒 Decision 7500 (U2b): the merges made, each with Undo Merge for its ninety days.
  { id: 'merged', name: t('duplicates.view.merged') },
] as const;

type ReportView = 'companies' | 'people' | 'merged';
const viewOf = (id: string | null): ReportView => (id === 'people' || id === 'merged' ? id : 'companies');

function columnsFor(view: 'companies' | 'people'): readonly ColumnDef[] {
  const name = view === 'companies' ? t('duplicates.col.company') : t('duplicates.col.person');
  const root = view === 'companies' ? '/companies' : '/people';
  return [
    { key: 'first', label: name, role: 'primary', type: 'link', linkPattern: `${root}/:firstId` },
    { key: 'second', label: t('duplicates.col.pair'), role: 'secondary', type: 'link', linkPattern: `${root}/:secondId` },
    { key: 'reason', label: t('duplicates.col.reason'), role: 'secondary', type: 'text' },
    { key: 'flags', label: view === 'companies' ? t('duplicates.col.flags') : t('duplicates.col.companies'), role: 'secondary', type: 'text' },
    ...(view === 'companies'
      ? ([
        { key: 'sites', label: t('duplicates.col.sites'), role: 'secondary', type: 'text', fit: true },
        { key: 'documents', label: t('duplicates.col.documents'), role: 'secondary', type: 'text', fit: true },
      ] as const)
      : []),
    { key: 'created', label: t('duplicates.col.created'), role: 'secondary', type: 'text', fit: true },
    { key: 'newerBy', label: t('duplicates.col.newerBy'), role: 'secondary', type: 'text', fit: true },
    { key: 'action', label: t('duplicates.col.action'), role: 'secondary', type: 'link', linkPattern: `${MERGE_ROUTE_PREFIX}:key`, fit: true },
  ];
}

const flagWord = (flag: string): string =>
  flag === 'Customer' ? t('duplicates.flag.customer') : flag === 'Vendor' ? t('duplicates.flag.vendor') : flag === 'Manufacturer' ? t('duplicates.flag.manufacturer') : flag;
export const flagsOf = (facts: DuplicateRecordFacts): string => (facts.flags.length > 0 ? facts.flags.map(flagWord).join(', ') : t('duplicates.none'));
export const createdByOf = (facts: DuplicateRecordFacts): string => (facts.createdBy === 'import' ? t('duplicates.createdBy.import') : t('duplicates.createdBy.here'));

/** The newer of the two, read off the created date and then the id, which is time-ordered. */
function newerOf(row: DuplicatePairRow): DuplicateRecordFacts {
  if (row.first.createdOn !== row.second.createdOn) return row.first.createdOn > row.second.createdOn ? row.first : row.second;
  return row.first.id > row.second.id ? row.first : row.second;
}

/** How the report reads a cell and names a row, the one pair the list draws and the merge window's walk sorts by. */
const readLine = (one: PairLine, column: string): unknown => (one as unknown as Record<string, unknown>)[column] ?? '';
const idOfLine = (one: PairLine): string => one.key;

/**
 * 🔒 The pair the merge window walks to (decision 3162): the first of the keys
 * after the one it stood on, in the order the report lists, that the report
 * still holds and that waits on no approval. Null where none is left.
 */
function nextPair(after: readonly string[], lines: readonly PairLine[]): DuplicatePairRow | null {
  for (const key of after) {
    const row = lines.find((one) => one.key === key)?.row;
    if (row && !row.awaitingApproval) return row;
  }
  return null;
}

function lineOf(row: DuplicatePairRow): PairLine {
  return {
    key: row.key,
    first: row.first.name,
    firstId: row.first.id,
    second: row.second.name,
    secondId: row.second.id,
    reason: row.reason,
    flags: line(flagsOf(row.first), '·', flagsOf(row.second)),
    sites: line(row.first.siteCount ?? 0, '·', row.second.siteCount ?? 0),
    documents: line(row.first.openDocuments ?? 0, '·', row.second.openDocuments ?? 0),
    created: line(row.first.createdOn, '·', row.second.createdOn),
    newerBy: createdByOf(newerOf(row)),
    action: row.awaitingApproval ? t('duplicates.merge.awaiting') : row.refusal ? t('duplicates.action.refused') : t('duplicates.action.merge'),
    row,
  };
}

export function DuplicatesPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { firstId: pairFirstId, secondId: pairSecondId } = useParams();
  /** T3422: the pair the address names, whichever order its two ids stand in. */
  const pairOnly = useMemo(() => (pairFirstId && pairSecondId ? new Set([pairFirstId, pairSecondId]) : null), [pairFirstId, pairSecondId]);
  const fetched = useApi<DuplicateReportAnswer>(DUPLICATES_PATH, apiBaseUrl);
  // 🔒 Decision 7500: the import's Step through opens the Merged view on one merge's window by the address.
  const [view, setView] = useState<ReportView>(viewOf(params.get('view')));
  const [undoing, setUndoing] = useState<string | null>(params.get('undo'));
  const [sort, setSort] = useState<SortState | null>(null);
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const [notice, setNotice] = useState<{ tone: 'good' | 'error'; text: string } | null>(null);
  const [merging, setMerging] = useState<DuplicatePairRow | null>(null);
  const [allExact, setAllExact] = useState(false);
  /** 🔒 The pairs opened in place (decision 4811): a click or Enter on a row shows its whole reason, again closes it. */
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const answer = fetched.state.kind === 'ready' ? fetched.state.data : null;
  const lines = useMemo(() => (answer && view !== 'merged' ? (view === 'companies' ? answer.companies : answer.people).map(lineOf).filter((one) => !pairOnly || (pairOnly.has(one.firstId) && pairOnly.has(one.secondId))) : []), [answer, view, pairOnly]);
  /** The pairs in the order the report lists them, sorted as the person sorted it: the order Merge and Next and Skip walk (decision 3162). */
  const listed = useMemo(() => sortRows(lines, sort, readLine, idOfLine), [lines, sort]);
  const keysAfter = (key: string): string[] => listed.slice(listed.findIndex((one) => one.key === key) + 1).map((one) => one.key);
  /**
   * 🔒 Decision 3162: after a Merge and Next the report is read again, and the
   * window opens on the first pair listed after the merged one that the fresh
   * read still holds; null on the last, or where the read fails.
   */
  const nextAfterMerge = async (key: string): Promise<DuplicatePairRow | null> => {
    const after = keysAfter(key);
    const fresh = await get<DuplicateReportAnswer>(DUPLICATES_PATH, apiBaseUrl);
    if (!fresh.ok) return null;
    return nextPair(after, (view === 'companies' ? fresh.data.companies : fresh.data.people).map(lineOf));
  };

  const done = (result: { ok: true; data: MergeAnswer } | { ok: false; message: string }) => {
    if (!result.ok) {
      setNotice({ tone: 'error', text: result.message });
      return;
    }
    setNotice({ tone: 'good', text: result.data.outcome === 'Merged' ? t('duplicates.merge.merged') : line(t('duplicates.merge.requested'), result.data.escalationReason ? `· ${result.data.escalationReason}` : null) });
    setMerging(null);
    setAllExact(false);
    fetched.reload();
  };

  const openRoute = (route: string) => {
    if (route.startsWith(UNDO_ROUTE_PREFIX)) {
      setUndoing(decodeURIComponent(route.slice(UNDO_ROUTE_PREFIX.length)));
      return;
    }
    if (!route.startsWith(MERGE_ROUTE_PREFIX)) {
      navigate(route);
      return;
    }
    const key = decodeURIComponent(route.slice(MERGE_ROUTE_PREFIX.length));
    const row = lines.find((one) => one.key === key)?.row ?? null;
    if (row && !row.awaitingApproval) setMerging(row);
  };

  const exactCount = answer?.exactCount ?? 0;
  return (
    <PageModule
      title={t('duplicates.title')}
      subtitle={t('duplicates.subtitle')}
      newAction={<Button size={Size.Medium} color={Color.Navy} text={[t('duplicates.mergeAllExact')]} disabled={exactCount === 0} disabledReason={t('duplicates.mergeAllExact.none')} actions={[() => setAllExact(true)]} />}
    >
      <div className={styles.page} data-page="duplicates">
        {notice ? <AlertBanner tone={notice.tone} message={notice.text} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
        {view === 'merged' ? (
          <MergedView apiBaseUrl={apiBaseUrl} views={VIEWS} onViewChange={(id) => setView(viewOf(id))} onRoute={openRoute} />
        ) : (
        <>
        {pairOnly ? (
          <div className={styles.hint} data-duplicates-pair={`${pairFirstId},${pairSecondId}`}>
            {t('duplicates.pair.hint')} <Button size={Size.Small} text={[t('duplicates.pair.all')]} actions={[() => navigate(DUPLICATES_ROUTE)]} />
          </div>
        ) : null}
        <p className={styles.hint} data-duplicates-exact={exactCount}>{exactCount > 0 ? line(exactCount, t('duplicates.mergeAllExact.count')) : t('duplicates.mergeAllExact.none')}</p>
        <AsyncStatePanel state={fetched.state} emptyAction={t('duplicates.empty.companies')} onRetry={fetched.reload}>
          {() => (
            <div data-duplicates-view={view} data-duplicates-rows={lines.length}>
              <ListViewer<PairLine>
                viewStore={{ scopeKey: viewerScopeKey('duplicates'), apiBaseUrl }}
                recordType={t('duplicates.title')}
                views={VIEWS}
                currentViewId={view}
                onViewChange={(id) => setView(viewOf(id))}
                columns={columnsFor(view)}
                rows={lines}
                idOf={idOfLine}
                read={readLine}
                sort={sort}
                onSortChange={setSort}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                emptyAction={pairOnly ? t('duplicates.pair.none') : view === 'companies' ? t('duplicates.empty.companies') : t('duplicates.empty.people')}
                selectable={false}
                onRoute={openRoute}
                expandRow={{
                  isOpen: (one) => opened.has(one.key),
                  onToggle: (one) => setOpened((now) => {
                    const next = new Set(now);
                    if (!next.delete(one.key)) next.add(one.key);
                    return next;
                  }),
                }}
              />
            </div>
          )}
        </AsyncStatePanel>
        </>
        )}
      </div>
      {undoing ? (
        <UndoMergeWindow
          key={undoing}
          mergeId={undoing}
          apiBaseUrl={apiBaseUrl}
          onCancel={() => setUndoing(null)}
          onUndone={(answer) => {
            setUndoing(null);
            navigate(answer.route);
          }}
        />
      ) : null}
      {merging ? (
        <MergeModal
          row={merging}
          onCancel={() => setMerging(null)}
          apiBaseUrl={apiBaseUrl}
          onMerge={async (keepId, fromMerged, andNext) => {
            const losingId = keepId === merging.first.id ? merging.second.id : merging.first.id;
            const result = await post<MergeAnswer>(recordMergePath(merging.recordType, losingId), { intoId: keepId, reason: merging.reason, fromMerged }, apiBaseUrl);
            // 🔒 Decision 3162: a refusal stays in the window, on the pair that refused.
            if (!result.ok) return result.message;
            setNotice({ tone: 'good', text: result.data.outcome === 'Merged' ? t('duplicates.merge.merged') : line(t('duplicates.merge.requested'), result.data.escalationReason ? `· ${result.data.escalationReason}` : null) });
            setMerging(andNext ? await nextAfterMerge(merging.key) : null);
            fetched.reload();
            return null;
          }}
          onSkip={() => {
            const next = nextPair(keysAfter(merging.key), lines);
            if (next) setMerging(next);
            else {
              setMerging(null);
              setNotice({ tone: 'good', text: t('duplicates.merge.lastPair') });
            }
          }}
          onNotSame={async () => {
            const result = await post(DUPLICATES_DISMISSALS_PATH, { recordType: merging.recordType, firstId: merging.first.id, secondId: merging.second.id }, apiBaseUrl);
            if (!result.ok) {
              setNotice({ tone: 'error', text: result.message });
              return;
            }
            setNotice({ tone: 'good', text: t('duplicates.merge.dismissed') });
            setMerging(null);
            fetched.reload();
          }}
        />
      ) : null}
      <Modal
        open={allExact}
        title={t('duplicates.mergeAllExact.title')}
        onCancel={() => setAllExact(false)}
        dataModal="duplicates-merge-all-exact"
        actions={[
          { kind: 'primary', label: t('duplicates.mergeAllExact.send'), onAct: () => void post<MergeAnswer>(DUPLICATES_MERGE_ALL_EXACT_PATH, {}, apiBaseUrl).then(done), disabled: exactCount === 0 },
          { kind: 'cancel', label: t('modal.cancel'), onAct: () => setAllExact(false) },
        ]}
      >
        <div className={styles.body}>
          <p>{t('duplicates.mergeAllExact.body')}</p>
          <p className={styles.hint} data-merge-all-count={exactCount}>{line(exactCount, t('duplicates.mergeAllExact.count'))}</p>
        </div>
      </Modal>
    </PageModule>
  );
}

export const MOVE_TITLES: Readonly<Record<MergeMoveList['kind'], string>> = {
  documents: t('duplicates.merge.moves.documents'),
  sites: t('duplicates.merge.moves.sites'),
  people: t('duplicates.merge.moves.people'),
  employments: t('duplicates.merge.moves.employments'),
  // Decision 7470: an item's moves, read by the Items list's Merge window.
  stock: t('duplicates.merge.moves.stock'),
  documentLines: t('duplicates.merge.moves.documentLines'),
  bomLines: t('duplicates.merge.moves.bomLines'),
  suppliers: t('duplicates.merge.moves.suppliers'),
  prices: t('duplicates.merge.moves.prices'),
};

/**
 * Pick the survivor (decision 1484): both records side by side, the proposed
 * one chosen, and the person may choose the other. A record rows that never
 * change still name cannot be the one merged away, so choosing to keep the
 * other is refused before anything is sent.
 *
 * 🔒 Then the fields and the preview (decision 1491): every field the two fill
 * differently, the survivor's value picked first, and by name what moves from
 * the record merged away. Merge waits until the preview has answered.
 *
 * 🔒 Drawn as one table (decision 2501): a column per record with the keep
 * control in its head, a row per differing field with the pick in its cell,
 * and What moves to the one that stays beneath.
 *
 * 🔒 It walks the report (decision 3162): Merge and Next, the Primary's further
 * choice, merges and opens the next pair, and Skip opens the next leaving this
 * one as it is. 🔨 The window stays open through the walk and is never keyed to
 * the pair, so the choice stays chosen; what it holds for a pair is held with
 * the pair's key and starts afresh on the next. Drawn through the Form frame
 * (decision 3060), so a refusal stands inside it.
 */
function MergeModal({ row, apiBaseUrl, onCancel, onMerge, onSkip, onNotSame }: {
  row: DuplicatePairRow;
  apiBaseUrl: string;
  onCancel: () => void;
  /** Merges, and answers the refusal in words or null. */
  onMerge: (keepId: string, fromMerged: string[], andNext: boolean) => Promise<string | null>;
  onSkip: () => void;
  onNotSame: () => Promise<void>;
}) {
  const [kept, setKept] = useState({ key: row.key, id: row.proposedSurvivorId });
  const keepId = kept.key === row.key ? kept.id : row.proposedSurvivorId;
  const setKeepId = (id: string) => setKept({ key: row.key, id });
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<{ key: string; text: string } | null>(null);
  /** The fields whose value is taken from the record merged away, held for the pair and the survivor they were picked against. */
  const [picks, setPicks] = useState<{ key: string; keepId: string; fromMerged: readonly string[] }>({ key: row.key, keepId: row.proposedSurvivorId, fromMerged: [] });
  const loser = keepId === row.first.id ? row.second : row.first;
  const company = row.recordType === 'CompanyRecord';
  const preview = useApi<MergePreviewAnswer>(mergePreviewPath(row.recordType, loser.id, keepId), apiBaseUrl);
  const ready = preview.state.kind === 'ready' ? preview.state.data : null;
  const conflicts = ready ? mergeFieldConflicts(row.recordType, ready.survivor, ready.merged) : [];
  const fromMerged = picks.key === row.key && picks.keepId === keepId ? picks.fromMerged.filter((key) => conflicts.some((conflict) => conflict.key === key)) : [];
  const pick = (key: string, side: string) => setPicks({ key: row.key, keepId, fromMerged: side === 'merged' ? [...fromMerged, key] : fromMerged.filter((one) => one !== key) });
  const blocked = row.refusal !== null || loser.fixed;
  const merge = async (andNext: boolean) => {
    setBusy(true);
    setRefused(null);
    const refusal = await onMerge(keepId, [...fromMerged], andNext);
    setBusy(false);
    if (refusal) setRefused({ key: row.key, text: refusal });
  };
  // 🔒 Decision 2501: a column per record, its facts and the keep control in its head.
  const facts = (record: DuplicateRecordFacts) => (
    <dl className={styles.facts}>
      <dt>{company ? t('duplicates.merge.flags') : t('duplicates.merge.worksAt')}</dt>
      <dd>{flagsOf(record)}</dd>
      {company ? (
        <>
          <dt>{t('duplicates.merge.owner')}</dt>
          <dd>{record.ownerName ?? t('duplicates.none')}</dd>
          <dt>{t('duplicates.merge.sites')}</dt>
          <dd>{record.siteCount ?? 0}</dd>
          <dt>{t('duplicates.merge.documents')}</dt>
          <dd>{record.openDocuments ?? 0}</dd>
        </>
      ) : null}
      <dt>{t('duplicates.merge.created')}</dt>
      <dd>{record.createdOn}</dd>
      <dt>{t('duplicates.merge.createdBy')}</dt>
      <dd>{createdByOf(record)}</dd>
    </dl>
  );
  const note = (record: DuplicateRecordFacts) => (
    <>
      {record.id === row.proposedSurvivorId ? <p className={styles.hint} data-merge-proposed="true">{t('duplicates.merge.proposed')}</p> : null}
      {record.fixed ? <p className={styles.hint} data-merge-fixed="true">{t('duplicates.merge.fixed')}</p> : null}
    </>
  );
  const records = [row.first, row.second];
  // Each differing field's two values, in the column of the record that holds each.
  const rows = conflicts.map((conflict) => ({
    key: conflict.key,
    label: conflict.label,
    values: Object.fromEntries(records.map((record) => [record.id, mergeFieldText(conflict.field, record.id === keepId ? conflict.survivor : conflict.merged)])),
  }));
  return (
    <FormFrame
      host="window"
      open
      title={company ? t('duplicates.merge.titleCompany') : t('duplicates.merge.titlePerson')}
      onCancel={onCancel}
      width="wide"
      dataForm="duplicates-merge"
      act={{
        label: t('duplicates.action.merge'),
        onAct: () => merge(false),
        disabled: busy || blocked || ready === null,
        disabledReason: blocked ? (row.refusal ?? t('duplicates.merge.fixed')) : t('duplicates.merge.movesLoading'),
        more: [{ label: t('duplicates.merge.mergeAndNext'), onAct: () => merge(true) }],
      }}
      refusal={refused?.key === row.key ? refused.text : null}
      onDismissRefusal={() => setRefused(null)}
      typed={fromMerged.length > 0 || keepId !== row.proposedSurvivorId}
    >
      <div className={styles.body}>
        <p>{t('duplicates.merge.lead')}</p>
        {row.refusal ? <AlertBanner tone="info" message={row.refusal} /> : null}
        <p className={styles.hint}>{line(t('duplicates.merge.why'), '·', row.reason)}</p>
        <section className={styles.section} data-merge-fields={ready ? conflicts.length : 'loading'}>
          {ready && conflicts.length > 0 ? <p className={styles.hint}>{t('duplicates.merge.fieldsLead')}</p> : null}
          <MergeTable
            columns={records.map((record) => ({ id: record.id, name: record.name, facts: facts(record), note: note(record) }))}
            keepId={keepId}
            onKeep={setKeepId}
            keepLabel={t('duplicates.merge.keep')}
            fieldLabel={t('duplicates.merge.fieldsTitle')}
            rows={rows}
            pickOf={(key) => (fromMerged.includes(key) ? loser.id : keepId)}
            onPick={(key, id) => pick(key, id === keepId ? 'survivor' : 'merged')}
            empty={ready === null ? null : t('duplicates.merge.fieldsNone')}
            disabled={busy}
          />
          {ready && ready.goesToApprover && fromMerged.length > 0 ? <p className={styles.hint} data-merge-approval-note="true">{t('duplicates.merge.fieldsApproval')}</p> : null}
        </section>
        <section className={styles.section} data-merge-moves-state={preview.state.kind}>
          <h3 className={styles.sideTitle}>{t('duplicates.merge.movesTitle')}</h3>
          {preview.state.kind === 'failed' ? <AlertBanner tone="error" message={preview.state.error} action={{ label: t('async.retry'), onAction: preview.reload }} /> : null}
          {ready === null ? (preview.state.kind === 'failed' ? null : <p className={styles.hint}>{t('duplicates.merge.movesLoading')}</p>) : (
            <div className={styles.moves}>
              {ready.moves.map((list) => (
                <div key={list.kind} className={styles.moveList} data-merge-moves={list.kind} data-merge-moves-count={list.names.length + list.more}>
                  <h4 className={styles.moveTitle}>{line(MOVE_TITLES[list.kind], '·', list.names.length + list.more)}</h4>
                  {list.names.length === 0 ? <p className={styles.hint}>{t('duplicates.merge.movesNone')}</p> : (
                    <ul className={styles.names}>
                      {list.names.map((name) => <li key={name.id} data-merge-move={name.id}>{name.label}</li>)}
                    </ul>
                  )}
                  {list.more > 0 ? <p className={styles.hint} data-merge-moves-more={list.more}>{line(list.more, t('duplicates.merge.movesMore'))}</p> : null}
                </div>
              ))}
            </div>
          )}
        </section>
        <div className={styles.toolbar} data-merge-walk="true">
          <Button size={Size.Small} color={Color.Inactive} text={[t('duplicates.merge.skip')]} disabled={busy} actions={[onSkip]} />
          <Button size={Size.Small} color={Color.Inactive} text={[t('duplicates.merge.notSame')]} disabled={busy} actions={[() => { setBusy(true); void onNotSame().finally(() => setBusy(false)); }]} />
          <span className={styles.hint}>{line(t('duplicates.merge.skipHint'), t('duplicates.merge.notSameHint'))}</span>
        </div>
      </div>
    </FormFrame>
  );
}
